import { Component, Input } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, map, skip, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { Store, select } from '@ngrx/store';
import * as RevisionActions from "../../stores/global/revisions/actions";
import * as RevisionStatusActions from "../../stores/global/revision-status/actions";
import * as CompanyActions from "../../stores/global/companies/company-actions";
import { IBaseSearchRequest, IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { revisionStatusSelector } from 'src/app/stores/global/revision-status/selectors';
import { RevisionService } from '../services/revision.service';
import { KENDO_INDICATORS } from "@progress/kendo-angular-indicators";
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';


@UntilDestroy()
@Component({
    selector: 'app-revisions-view-admin',
    templateUrl: './revisions-view-admin.component.html',
    styleUrl: './revisions-view-admin.component.scss',
    standalone: false
})
export class RevisionsViewAdminComponent {

  ////////////////
  // variables //
  //////////////

  @Input() reloadResults : boolean = true;
  keyword : string;
  selectedCategoryId : string;  
  vm$: Observable<any>;
  defaultStatus: string[] = [];
  private isResettingFilter = false;
  loading: boolean = false;


  // Create filter form
  filterForm = this.fb.group({
    companyId: new UntypedFormControl(null),
    revisionStatusIds: new UntypedFormControl([]),
    keyword: new FormControl(null),
    executionDateFrom: new UntypedFormControl(null),
    executionDateTo: new UntypedFormControl(null),
  });

  // Grid Form
  public formGroup = new UntypedFormGroup({
    companyId: new UntypedFormControl(''),
    revisionStatusIds: new UntypedFormControl([]),
    executionDateFrom: new UntypedFormControl(null),
    executionDateTo: new UntypedFormControl(null),
    orderNumber: new UntypedFormControl('')
  });

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };

  constructor(
    private fb : UntypedFormBuilder,
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private revisionService: RevisionService,
    private router: Router
  ) {
    this.vm$ = combineLatest([ 
      this.gridState$.pipe(),
      this.searchRequest$.pipe(startWith(null)),
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
        map(([pageChangeEvent, searchRequest]) => ({
          pageChangeEvent,
          searchRequest,
        })),
        tap((x) => {
          const pageNumber = Math.round(
            (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
              x.pageChangeEvent.take
          );
   
          this.gridState$.next({
            skip: x.pageChangeEvent.skip,
            pageSize: x.pageChangeEvent.take,
          });
   
          this.searchRequest$.next({
            ...x.searchRequest,
            pageNumber: pageNumber,
            pageSize: x.pageChangeEvent.take,
          });
        }),
        startWith({ skip: 0, take: 10 })
      ),
      this.store.pipe(select(companySelector)),
      this.store.pipe(select(revisionStatusSelector)).pipe(tap((res) => {
        this.defaultStatus = [];
        res.forEach(status => {
          if (status.isFinalStatus == false)
          this.defaultStatus.push(status.id);
        });
  
        if (this.defaultStatus.length > 0 && this.filterForm.get('revisionStatusIds')?.value?.length == 0) {
          this.filterForm.get('revisionStatusIds')?.setValue(this.defaultStatus);
          this.updateUrlWithQuery(this.filterForm.value)
        }
      })),
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({params, searchRequest})),
        tap(data => {
          if(JSON.stringify(data.params) !== "{}")
          {
            this.onSubmit(data.searchRequest)
          }
        }),
        take(1) // only execute once
      ),
      // Order is very Important!
      this.searchRequest$.pipe(
        switchMap((request) =>{
          this.loading = true;
          return this.revisionService.getRevisions(request);
        }),
        tap(() => {
          this.loading = false; // Stop loading when data is fetched
        }),
        map(x => {
          var gridData : GridDataResult = {
            data: x.data,
            total: x.totalCount
          }
          return gridData
        }),
        startWith(null)
      ),
    ]).pipe(
      map(([
        gridState,
        searchRequest,
        pageChangeSub,
        companies,
        revisionStatus,
        filterForm,
        revisions,
      ]) => ({
        gridState,
        searchRequest,
        pageChangeSub,
        companies,
        revisionStatus,
        filterForm,
        revisions,
      }))
    );
  }

  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  ngOnInit(): void {
    // get data from stores
    // this.store.dispatch(RevisionActions.getRevisions()); //TODO: why is this needed? JE
    this.store.dispatch(RevisionStatusActions.getRevisionStatus());
    this.store.dispatch(CompanyActions.getCompanies());

    // Listen for changes to the filter form values and call onSubmit
    this.filterForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!this.isResettingFilter) {
          const currentRequest = this.searchRequest$.getValue();
          this.onSubmit(currentRequest);
        }
      });
  }

  sanitizeCreationDate(inputdate: any) {
    if (!inputdate) {
      return "-";
    }

    const date = new Date(inputdate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  patchFilterForm(params: any) {
    if(Object.keys(params).length > 0) {
      this.filterForm.patchValue({
        companyId: params['companyId'] || null,
        revisionStatusIds: params['revisionStatusIds']? params['revisionStatusIds'].split(',') : [],
        keyword: params['keyword'] || null,
        executionDateFrom: parseDDMMYYYYToDate(params['executionDateFrom']),
        executionDateTo: parseDDMMYYYYToDate(params['executionDateTo']),
      });
    }
  }
  

  ///////////////
  // getters  //
  /////////////

  get pageSizes() {
    return PageSize;
  }

  getControl(path: string) {
    return this.filterForm.get(path) as FormControl;
  }

  
  /////////////////
  // functions  //
  ///////////////

  onSubmit(request: IBaseSearchRequest): void {
    var formValues = this.filterForm.value;

    const searchRequest: IBaseSearchRequest = {
      ...request,
    };

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['orderNumber'];

    if (!keyword && !formValues.companyId && !formValues.revisionStatusIds && !formValues.executionDateFrom && !formValues.executionDateTo) {
      this.resetFilter()
      return;
    }

    // Create keyword filters
    const keywordFilters: IFilter[] = keyword
        ? searchFields.map(field => ({ field, operator: 'contains', value: keyword }))
        : [];

    // Create revision status filters
    const revisionStatusFilter: IFilter | null = Array.isArray(formValues.revisionStatusIds) && formValues.revisionStatusIds.length > 0
      ?  { logic: 'or', filters: formValues.revisionStatusIds.map((id) => ({ field: 'revisionStatus.id', operator: 'eq', value: id }))}
      : null;

    const companyFilter: IFilter[] = formValues.companyId 
      ? [{ field: 'company.id', operator: 'eq', value: formValues.companyId }]
      : [];

    const dateFromFilter: IFilter[] = formValues.executionDateFrom 
      ? [{ field: 'scheduledDate', operator: 'gte', value: convertFrontendDateToBackendFormat(formValues.executionDateFrom) }]
      : [];

    const dateToFilter: IFilter[] = formValues.executionDateTo 
      ? [{ field: 'scheduledDate', operator: 'lte', value: convertFrontendDateToBackendFormat(formValues.executionDateTo) }]
      : [];

    // Combine all filters into an array
    const allFilters: IFilter[] = [
      ...keywordFilters,
      ...(revisionStatusFilter ? [revisionStatusFilter] : []),
      ...companyFilter,
      ...dateFromFilter,
      ...dateToFilter,
    ];

    // Ensure the final filter object has at least one valid filter
    if (allFilters.length === 0) {
      this.resetFilter();
      return;
    }

    // Final filter object
    const filterObject: IFilter = {
      logic: 'and',
      filters: allFilters,
    };

    filterObject.filters = allFilters;

    searchRequest.advancedFilter = filterObject;

    this.searchRequest$.next(searchRequest);

    this.updateUrlWithQuery(formValues);
  }

  updateUrlWithQuery(formValues){
    // Update URL with the filter parameters
    const queryParams = {
      keyword: formValues.keyword || null,
      companyId: formValues.companyId || null,
      revisionStatusIds: formValues.revisionStatusIds ? formValues.revisionStatusIds.join(',') : null,
      executionDateFrom: formatDateToDDMMYYYY(formValues.executionDateFrom),
      executionDateTo: formatDateToDDMMYYYY(formValues.executionDateTo)    
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  resetFilter(): void {
    this.isResettingFilter = true; 
  
    this.filterForm.reset();

    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})
  
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  
    this.isResettingFilter = false;
  }
}

function checkProperties(obj: Record<string, any>): boolean {
  return Object.values(obj).every(value => value === null);
}

// Helper to format dates as ddMMyyyy
function formatDateToDDMMYYYY(date: Date | null): string | null {
  if (!date) return null;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

// Helper to parse ddMMyyyy to Date
function parseDDMMYYYYToDate(dateStr: string | null): Date | null {
  if (!dateStr || dateStr.length !== 8) return null; // Ensure the string is valid
  const day = parseInt(dateStr.substring(0, 2), 10);
  const month = parseInt(dateStr.substring(2, 4), 10) - 1; // Month is 0-indexed
  const year = parseInt(dateStr.substring(4, 8), 10);
  return new Date(year, month, day);
};

const convertFrontendDateToBackendFormat = (feDate: string): string => {
  // Parse the frontend UTC date (e.g., "2024-12-01T00:00:00.000Z")
  const utcDate = new Date(feDate);

  // Adjust the UTC date to the desired timezone (CET = UTC +01:00)
  const offsetInMilliseconds = 1 * 60 * 60 * 1000; // 1 hour in milliseconds
  const cetDate = new Date(utcDate.getTime() + offsetInMilliseconds);

  // Format the CET date to match the backend format "yyyy-MM-ddTHH:mm:ss+01:00"
  const year = cetDate.getUTCFullYear();
  const month = String(cetDate.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
  const day = String(cetDate.getUTCDate()).padStart(2, '0');
  const hours = String(cetDate.getUTCHours()).padStart(2, '0');
  const minutes = String(cetDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(cetDate.getUTCSeconds()).padStart(2, '0');

  // Return in the desired format: "yyyy-MM-ddTHH:mm:ss+01:00"
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+01:00`;
};